import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BlacklistedUser } from '../types';

const Blacklist: React.FC = () => {
  const { shop, user } = useAuth();
  const [blacklistedUsers, setBlacklistedUsers] = useState<BlacklistedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ダイアログ
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BlacklistedUser | null>(null);

  // フォームデータ
  const [formData, setFormData] = useState({
    lineUserId: '',
    phoneNumber: '',
    firebaseUid: '',
    displayName: '',
    reason: '',
    blockDuration: 'permanent' as 'permanent' | '1week' | '1month',
  });

  const [unblockReason, setUnblockReason] = useState('');

  useEffect(() => {
    if (shop) {
      fetchBlacklistedUsers();
    }
  }, [shop]);

  const fetchBlacklistedUsers = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'blacklistedUsers'),
        where('shopId', '==', shop.id),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        unblockedAt: doc.data().unblockedAt?.toDate(),
        unblockAt: doc.data().unblockAt?.toDate(),
      })) as BlacklistedUser[];
      setBlacklistedUsers(data);
    } catch (error) {
      console.error('ブラックリスト取得エラー:', error);
      setError('ブラックリストの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlacklist = async () => {
    if (!shop || !user) return;

    // 最低1つの識別子が必要
    if (!formData.lineUserId && !formData.phoneNumber && !formData.firebaseUid) {
      setError('LINE ID、電話番号、Firebase UIDのいずれか1つは必須です');
      return;
    }

    if (!formData.reason) {
      setError('ブロック理由を入力してください');
      return;
    }

    try {
      const now = Timestamp.now();
      let unblockAt = null;

      if (formData.blockDuration === '1week') {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        unblockAt = Timestamp.fromDate(date);
      } else if (formData.blockDuration === '1month') {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        unblockAt = Timestamp.fromDate(date);
      }

      const blacklistData: any = {
        shopId: shop.id,
        reason: formData.reason,
        blockedByName: user.displayName || user.email || '管理者',
        blockedByUid: user.uid,
        blockedByRole: 'owner', // Shop dashboard users are owners
        relatedOrderIds: [],
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };

      if (formData.lineUserId) blacklistData.lineUserId = formData.lineUserId;
      if (formData.phoneNumber) blacklistData.phoneNumber = formData.phoneNumber;
      if (formData.firebaseUid) blacklistData.firebaseUid = formData.firebaseUid;
      if (formData.displayName) blacklistData.displayName = formData.displayName;
      if (unblockAt) blacklistData.unblockAt = unblockAt;

      await addDoc(collection(db, 'blacklistedUsers'), blacklistData);

      setSuccess('ユーザーをブロックしました');
      setAddDialogOpen(false);
      setFormData({
        lineUserId: '',
        phoneNumber: '',
        firebaseUid: '',
        displayName: '',
        reason: '',
        blockDuration: 'permanent',
      });
      fetchBlacklistedUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('ブロック追加エラー:', error);
      setError('ブロックに失敗しました');
    }
  };

  const handleUnblock = async () => {
    if (!selectedUser || !user) return;

    if (!unblockReason) {
      setError('ブロック解除理由を入力してください');
      return;
    }

    try {
      await updateDoc(doc(db, 'blacklistedUsers', selectedUser.id), {
        isActive: false,
        unblockedAt: Timestamp.now(),
        unblockedBy: user.uid,
        unblockedReason: unblockReason,
        updatedAt: Timestamp.now(),
      });

      setSuccess('ブロックを解除しました');
      setUnblockDialogOpen(false);
      setSelectedUser(null);
      setUnblockReason('');
      fetchBlacklistedUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('ブロック解除エラー:', error);
      setError('ブロック解除に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await deleteDoc(doc(db, 'blacklistedUsers', selectedUser.id));

      setSuccess('記録を削除しました');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchBlacklistedUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('削除エラー:', error);
      setError('削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">ブラックリスト管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          ユーザーをブロック
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ステータス</TableCell>
              <TableCell>ユーザー情報</TableCell>
              <TableCell>識別子</TableCell>
              <TableCell>ブロック理由</TableCell>
              <TableCell>ブロック日時</TableCell>
              <TableCell>ブロック者</TableCell>
              <TableCell>解除予定</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blacklistedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  ブロック中のユーザーはいません
                </TableCell>
              </TableRow>
            ) : (
              blacklistedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    {user.isActive ? (
                      <Chip label="ブロック中" color="error" size="small" />
                    ) : (
                      <Chip label="解除済み" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.photoUrl && (
                        <Avatar src={user.photoUrl} sx={{ width: 32, height: 32 }} />
                      )}
                      <Typography variant="body2">
                        {user.displayName || '名前なし'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {user.lineUserId && (
                        <Chip label={`LINE: ${user.lineUserId.substring(0, 10)}...`} size="small" variant="outlined" />
                      )}
                      {user.phoneNumber && (
                        <Chip label={`電話: ${user.phoneNumber}`} size="small" variant="outlined" />
                      )}
                      {user.firebaseUid && (
                        <Chip label={`UID: ${user.firebaseUid.substring(0, 10)}...`} size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.reason}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.createdAt.toLocaleDateString('ja-JP')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.blockedByName}</Typography>
                  </TableCell>
                  <TableCell>
                    {user.unblockAt ? (
                      <Typography variant="body2">
                        {user.unblockAt.toLocaleDateString('ja-JP')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        なし
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {user.isActive && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedUser(user);
                            setUnblockDialogOpen(true);
                          }}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ブロック追加ダイアログ */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザーをブロック</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Alert severity="warning">
              最低1つの識別子（LINE ID、電話番号、Firebase UID）を入力してください
            </Alert>

            <TextField
              label="LINE User ID"
              value={formData.lineUserId}
              onChange={(e) => setFormData({ ...formData, lineUserId: e.target.value })}
              fullWidth
              helperText="LINE連携時のUser ID"
            />

            <TextField
              label="電話番号"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              fullWidth
              helperText="+81形式で入力（例: +8190XXXXXXXX）"
            />

            <TextField
              label="Firebase UID"
              value={formData.firebaseUid}
              onChange={(e) => setFormData({ ...formData, firebaseUid: e.target.value })}
              fullWidth
              helperText="Firebase認証のUID"
            />

            <TextField
              label="表示名（任意）"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              fullWidth
              helperText="記録用の名前"
            />

            <TextField
              label="ブロック理由"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              multiline
              rows={3}
              required
              fullWidth
              helperText="例: イタズラ注文を繰り返し"
            />

            <FormControl fullWidth>
              <InputLabel>ブロック期間</InputLabel>
              <Select
                value={formData.blockDuration}
                label="ブロック期間"
                onChange={(e) => setFormData({ ...formData, blockDuration: e.target.value as any })}
              >
                <MenuItem value="permanent">永久</MenuItem>
                <MenuItem value="1week">1週間</MenuItem>
                <MenuItem value="1month">1ヶ月</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleAddBlacklist} variant="contained" color="error">
            ブロック
          </Button>
        </DialogActions>
      </Dialog>

      {/* ブロック解除ダイアログ */}
      <Dialog open={unblockDialogOpen} onClose={() => setUnblockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ブロック解除</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              {selectedUser?.displayName || '名前なし'}のブロックを解除しますか？
            </Typography>

            <TextField
              label="解除理由"
              value={unblockReason}
              onChange={(e) => setUnblockReason(e.target.value)}
              multiline
              rows={3}
              required
              fullWidth
              sx={{ mt: 2 }}
              helperText="例: 誤解が解けたため"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblockDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleUnblock} variant="contained" color="primary">
            解除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>記録を削除</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedUser?.displayName || '名前なし'}のブロック記録を完全に削除しますか？
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            この操作は取り消せません
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Blacklist;
